'use client'

import _ from 'lodash'
import { twMerge } from 'tailwind-merge'
import { useState, useEffect, useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Dialog, DialogPanel, DialogBackdrop } from '@headlessui/react'

import { Api, Vehicle } from '@cruz-com/lookup'
import { MagnifyingGlassIcon, ArrowUturnLeftIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { Button, Checkbox, Radio, Text, Toggle, ListBox, PageLoading } from '@/components'
import { vehicleApi } from '@/api'
import { MakeMode } from './vehicleMakeModel'
import { radiusOption } from '@/settings'

type Props = {
  type?: 'antique'
  ignoreFilters?: string[]
  defaultQuery: Vehicle.Query
  onFilterChanged: (query: Vehicle.Query) => void
}

const groupStyle = 'w-full divide-y border rounded-md bg-zinc-100/80 dark:bg-zinc-800/80'

export function VehicleFilterMin({ type, ignoreFilters, defaultQuery, onFilterChanged }: Props) {
  const [showFilter, setShowFilter] = useState(false)
  const [query, setQuery] = useState<Vehicle.Query | any>(defaultQuery)
  const [loc, setLoc] = useState<any>({ zip_code: '', radius: -1 })

  useEffect(() => {
    setTimeout(() => {
      setQuery(defaultQuery)
      setLoc(_.pick(defaultQuery, ['zip_code', 'radius']))
    }, 10)
  }, [defaultQuery])


  // get options
  const { data, isFetching, refetch } = useQuery<Api.Result<Vehicle.FilterOptions> | any>({
    queryKey: [`vehicleFilterOptions_${type || 'none'}`],
    queryFn: () => vehicleApi.getFilterOption(_.pickBy(query, _.identity), type),
    staleTime: 3000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false
  })

  // checkbox changed - multiple selection
  function checkboxChanged({ key, id, val }: { key: string, id: number, val: boolean }) {
    const x: string[] = query[key] ? _.split(query[key] as string, ',') : []
    const y = val ? _.concat(x, `${id}`) : _.without(x, `${id}`)
    if (_.isEmpty(y)) {
      return setQuery(_.omit(query, key))
    }
    setQuery({ ...query, [key]: _.join(y, ',') })
  }

  // (dropdown or radio) changed - single selection
  function radioChanged({ name, id }: { name: string, id: number }) {
    return setQuery({ ...query, [name]: id })
  }

  // toggle (switch) changed
  function toggleChanged(name: string, isEnabled: boolean) {
    if (isEnabled) {
      return setQuery({ ...query, [name]: 1 })
    }
    setQuery(_.omit(query, name))
  }

  // mmt (make, model) changed
  function mmChanged(name: string, val: string | null | any) {
    if (!_.get(query, name) && !val) { return }
    if (_.isEmpty(val)) {
      return setQuery(_.omit(query, name))
    }
    setQuery({ ...query, [name]: val })
  }

  // ------------------ Location ------------------
  function locChanged(name: string, value: any) {
    let updateValue = value
    if (name === 'zip_code') {
      updateValue = value.replace(/\D/g, '') // Remove all non-digit characters
    }
    const d = { ...loc, [name]: updateValue }
    setLoc(d)
    const zLen = _.size(d.zip_code)
    if (zLen === 5 || (zLen === 0 && _.size(loc.zip_code) > 0)) {
      if (d.radius > 0 || (d.radius < 0 && loc.radius > 0)) {
        setQuery({ ...query, ...d })
      }
    }
  }

  // apply filter
  function applyFilter() {
    setShowFilter(false)
    _.keys(query).forEach((key) => {
      (query[key] < 0) && delete query[key]
    })
    setTimeout(() => {
      onFilterChanged(query)
    }, 200)
  }

  // default query changed (reset query)
  useEffect(() => {
    if (_.isEmpty(defaultQuery) && !_.isEmpty(query)) {
      setQuery({})
    }
  }, [defaultQuery])

  // query changed - get new options
  useEffect(() => {
    refetch()
  }, [query])

  // data changed (fetched) - set query
  useEffect(() => {
    // remove unavailable filter options
    const qKeys = _.pull(_.keys(query), 'page_number', 'page_size', 'sort_id', 'sort_value')
    _.forEach(qKeys, (key) => {
      const lkp = _.get(data?.data, key)
      if (!lkp) {
        return
      }
      const q = _.split(query[key], ',')
      const nq: any[] = []
      _.forEach(q, (id) => {
        let x = _.find(lkp, d => { return d?.id?.toString() === id?.toString() })
        if (x) { nq.push(id) }
      })
      if (nq.length > 0) {
        _.assign(query, { [key]: _.join(nq, ',') })
      }
    })
  }, [data?.data])

  // options
  const {
    sale_status, price, mileage, year_min, year_max, make,
    fuel_type, seller
  } = data?.data || {}

  const yearOption = useCallback(() => {
    if (!year_min || !year_max) {
      return [{ id: -1, name: 'Any' }]
    }
    const d = _.map(_.range(year_max, year_min || 1900), (y) => {
      return { id: y, name: y.toString() }
    })
    return [{ id: -1, name: 'Any' }, ...d]
  }, [year_min, year_max])

  const SaleStatus = () =>
    ignoreFilters?.includes('sale_status') ? <></> :
      <div>
        <div className='filter-label-min mb-2'>Sale Status</div>
        <ul className='flex flex-col sm:flex-row gap-x-3 gap-y-1'>
          {_.map(sale_status, ({ id, name }) =>
            <li key={id}>
              <Checkbox name={name} label={name}
                checked={_.split(query?.sale_status, ',').includes(id?.toString())}
                onChange={(val) => checkboxChanged({ key: 'sale_status', id, val })}
              />
            </li>)}
        </ul>
      </div>

  const Year = () =>
    <div>
      <div className='filter-label-min mb-1.5'>Year</div>
      <div className='w-fit flex items-end gap-x-2'>
        <div className='w-28'>
          <ListBox compact={true} option={yearOption()} value={query?.year_min || -1} className='w-32'
            onChange={(val) => { radioChanged({ name: 'year_min', id: val as number }) }}
          />
        </div>
        <div className='font-light text-sm/8'>to</div>
        <div className='w-28'>
          <ListBox compact={true} option={yearOption()} value={query?.year_max || -1} className='w-32'
            onChange={(val) => { radioChanged({ name: 'year_max', id: val as number }) }}
          />
        </div>
      </div>
    </div>

  const Price = () =>
    <div>
      <div className='filter-label-min'>Price</div>
      <div className='w-fit flex items-end gap-x-4'>
        <div className='w-36'>
          <ListBox label={<span className='filter-input-label'>Minimum</span>} compact={true} option={price}
            value={query?.price_min || -1} className='w-32'
            onChange={(val) => { radioChanged({ name: 'price_min', id: val as number }) }}
          />
        </div>
        <div className='w-36'>
          <ListBox label={<span className='filter-input-label'>Maximum</span>} compact={true} option={price}
            value={query?.price_max || -1} className='w-32'
            onChange={(val) => { radioChanged({ name: 'price_max', id: val as number }) }}
          />
        </div>
      </div>
    </div>

  const Mileage = () =>
    <div className='w-64'>
      <div className='filter-label-min mb-2'>Mileage</div>
      <ListBox compact={true} option={mileage} value={query?.mileage || -1}
        disabled={_.size(mileage) < 2}
        onChange={(val) => { radioChanged({ name: 'mileage', id: val as number }) }}
      />
    </div>

  const FuelType = () =>
    ignoreFilters?.includes('fuel_type') ? <></> :
      <div>
        <div className='filter-label-min mb-2'>Fuel Type</div>
        <ul className='flex flex-col sm:flex-row gap-x-3 gap-y-1'>
          {_.map(fuel_type, ({ id, name }) =>
            <li key={id}>
              <Checkbox name={name} label={name}
                checked={_.split(query?.fuel_type, ',').includes(id.toString())}
                onChange={(val) => checkboxChanged({ key: 'fuel_type', id, val })} />
            </li>)}
        </ul>
      </div>

  const Seller = () =>
    ignoreFilters?.includes('seller') ? <></> :
      <div>
        <div className='filter-label-min mb-2'>Seller</div>
        {!_.isEmpty(seller) &&
          <ul className='flex flex-col md:flex-row gap-x-6'>
            {_.map(seller, ({ id, name }) =>
              <li key={id}>
                <Radio name='seller' id={id} label={name} checked={query?.seller?.toString() === id.toString()}
                  onChange={() => radioChanged({ name: 'seller', id })} />
              </li>)}
          </ul>}
      </div>

  const Location = () =>
    ignoreFilters?.includes('location') ? <></> :
      <div>
        <div className='filter-label'>Location</div>
        <div className='flex items-start gap-4 w-fit mt-2'>
          <div className='flex items-center'>
            <label htmlFor='zip_code' className='vehicle-s2-label mr-2'>Zip Code:</label>
            <div className='w-[70px]'>
              <Text inline={true} compact={true} name='zip_code' maxLength={5} type='tel'
                value={loc?.zip_code || ''}
                onChange={e => locChanged('zip_code', e.target.value)}
                onFocus={e => e.target.select()}
              />
            </div>
          </div>
          <div className='flex items-center'>
            <div className='vehicle-s2-label mr-2'>Radius:</div>
            <div className='w-[130px]'>
              <ListBox inline={true} compact={true}
                option={radiusOption}
                value={loc?.radius || -1}
                onChange={(val) => { locChanged('radius', val) }}
              />
            </div>
          </div>
        </div>
      </div>

  return (
    <>
      <Dialog transition={true} unmount={false} open={showFilter} onClose={() => setShowFilter(false)} className='relative z-30'>
        <DialogBackdrop transition={true}
          className={twMerge('fixed inset-0 bg-zinc-800/75 transition-opacity',
            'data-closed:opacity-0 data-enter:duration-300 data-leave:duration-200 data-enter:ease-out data-leave:ease-in')}
        />
        <div className='fixed inset-0 z-10 w-screen overflow-y-scroll'>
          <div className='mx-auto max-w-[480px]'>
            <DialogPanel className='bg-white dark:bg-zinc-900 rounded-lg m-4 relative'>
              <PageLoading className='top-2' show={isFetching} />
              <div className='flex flex-col gap-y-6 w-full p-6 md:p-8'>
                {Location()}
                <Year />
                {
                  !ignoreFilters?.includes('mm') &&
                  <div className='w-full'>
                    <div className='filter-label mb-3'>Make Model</div>
                    <div className={groupStyle}>
                      <MakeMode name='mm1' title='Make Model 1' type={type} makeOptions={make}
                        otherQuery={query} mm={query?.mm1} onChange={val => mmChanged('mm1', val)} />
                      <MakeMode name='mm2' title='Make Model 2' type={type} makeOptions={make}
                        otherQuery={query} mm={query?.mm2} onChange={val => mmChanged('mm2', val)} />
                      <MakeMode name='mm3' title='Make Model 3' type={type} makeOptions={make}
                        otherQuery={query} mm={query?.mm3} onChange={val => mmChanged('mm3', val)} className='rounded-b-md' />
                    </div>
                  </div>
                }
                <Price />
                <Mileage />
                <FuelType />
                <Seller />
                <SaleStatus />
                { /* toggle */}
                {
                  !ignoreFilters?.includes('with_photo') &&
                  <Toggle label='With Photo(s)' name='with_photo' checked={_.toNumber(query?.with_photo) === 1}
                    onChange={isEnabled => toggleChanged('with_photo', isEnabled)} />
                }
              </div>
              {/* search button  */}
              <div className='sticky bottom-0 z-20 bg-zinc-600/10 dark:bg-zinc-800/50 rounded-b-lg px-6 py-4 border-t backdrop-blur'>
                <div className='flex justify-between items-center gap-x-2'>
                  <Button type='button' variant='transparent' onClick={() => setQuery({})} aria-label='Reset'>
                    <ArrowUturnLeftIcon className='size-5 mr-2' />
                    Reset
                  </Button>
                  <Button type='button' variant='primary' className='px-6 rounded-full' onClick={applyFilter} aria-label='Search'>
                    <MagnifyingGlassIcon className='size-5 mr-2' />
                    Search
                  </Button>
                  <Button type='button' variant='transparent' onClick={() => setShowFilter(false)} aria-label='Cancel'>
                    <XMarkIcon className='size-5 mr-2' />
                    Cancel
                  </Button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
      <Button type='button' variant='secondary' className='pl-4 pr-5 rounded-full h-10 mt-4 mb-2 sm:mb-0' onClick={() => setShowFilter(true)}>
        <MagnifyingGlassIcon className='size-5 mr-2' />
        Search Filters
      </Button>
    </>
  )
}
